// frontend/src/hooks/useAuditTrail.ts — the audit trail read surface: the feed
// (cursor / "load older"), one line by id (the shareable peek), and the export's
// params. The FIRST cursor consumer (CONVENTIONS §Pagination): built on TanStack
// `useInfiniteQuery`, whose page-accumulation + `fetchNextPage` map directly onto
// the Timeline's onLoadOlder/hasOlder contract.
//
// The filters are the page's URL keys, one for one (ADR-037 §2): `period` + `tz`
// (ADR-082 §5 · §6 — the word or the day pair as the URL holds it, the PERSON's
// zone beside it; the browser never cuts the query) · `action` (the six words) ·
// `object` (the tree's tokens) · `person` (ids, or `me`) · `channel` · `q` (the
// subject's name) · `correlation` (the lines of one action). A page of 100 (the
// trail is generous before "Load older"; the schema's default of 25 is too few —
// architect-ruled), never auto-loaded.
//
// Server state per FRONTEND-PATTERNS §State: key is ['audit', projectId, 'list', filters]
// so changing any facet starts a fresh keyset stream; 401 stays the centralized
// apiClient's job (ADR-003).

import {
  useInfiniteQuery,
  useQuery,
  type InfiniteData,
  type QueryClient,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { AuditEvent, AuditListResponse } from '@ems-app/schemas/audit/responses';
import { apiClient } from '../lib/apiClient.js';

export const AUDIT_PAGE_SIZE = 100;

export interface AuditFilters {
  /** A period word or `YYYY-MM-DD..YYYY-MM-DD`; absent = all time. */
  period?: string;
  /** The screen's zone — the person's; rides only beside a period. */
  tz?: string;
  /** The six words (`created` · `changed` · `deleted` · `viewed` · `sign_in` · `system`). */
  action?: string[];
  /** The Object tree's tokens (`family:<f>` · `<object>` · `<object>.<verb>`). */
  object?: string[];
  /** User ids, or `me` for the viewer. */
  person?: string[];
  channel?: string[];
  /** The subject's name, as typed. */
  q?: string;
  /** The lines of one action (a correlation id). */
  correlation?: string;
  // Entity-scoped mode (the per-entity History subset) — both together.
  entity_type?: string;
  entity_id?: string;
}

export const auditKeys = {
  all: (projectId: string) => ['audit', projectId] as const,
  list: (projectId: string, filters: AuditFilters) => ['audit', projectId, 'list', filters] as const,
  event: (projectId: string, eventId: string) => ['audit', projectId, 'event', eventId] as const,
};

/** The filters as the wire takes them — the feed's and the export's one grammar; `tz` only beside a period. */
export function auditFilterParams(filters: AuditFilters): Record<string, string> {
  const p: Record<string, string> = {};
  if (filters.period) {
    p.period = filters.period;
    if (filters.tz) p.tz = filters.tz;
  }
  if (filters.action?.length) p.action = filters.action.join(',');
  if (filters.object?.length) p.object = filters.object.join(',');
  if (filters.person?.length) p.person = filters.person.join(',');
  if (filters.channel?.length) p.channel = filters.channel.join(',');
  if (filters.q) p.q = filters.q;
  if (filters.correlation) p.correlation = filters.correlation;
  if (filters.entity_type) p.entity_type = filters.entity_type;
  if (filters.entity_id) p.entity_id = filters.entity_id;
  return p;
}

async function fetchAuditPage(projectId: string, filters: AuditFilters, cursor: string | null): Promise<AuditListResponse> {
  const params: Record<string, string> = { ...auditFilterParams(filters), limit: String(AUDIT_PAGE_SIZE) };
  if (cursor) params.cursor = cursor;
  const r = await apiClient.get<AuditListResponse>(`/projects/${projectId}/audit`, { params });
  return r.data;
}

export function useAuditTrail(
  projectId: string,
  filters: AuditFilters,
  opts: { enabled?: boolean } = {},
): UseInfiniteQueryResult<InfiniteData<AuditListResponse>, Error> {
  return useInfiniteQuery({
    queryKey: auditKeys.list(projectId, filters),
    queryFn: ({ pageParam }) => fetchAuditPage(projectId, filters, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: projectId.length > 0 && (opts.enabled ?? true),
  });
}

/** Flatten accumulated pages into one newest-first list for the Timeline. */
export function flattenAudit(data: InfiniteData<AuditListResponse> | undefined): AuditEvent[] {
  return data?.pages.flatMap((p) => p.items) ?? [];
}

/** One line by id — the peek's read (`?peek=audit:<id>` survives a refresh and a paste). */
export function useAuditEvent(projectId: string, eventId: string): UseQueryResult<AuditEvent, Error> {
  return useQuery({
    queryKey: auditKeys.event(projectId, eventId),
    queryFn: async () => (await apiClient.get<AuditEvent>(`/projects/${projectId}/audit/${encodeURIComponent(eventId)}`)).data,
    enabled: projectId.length > 0 && eventId.length > 0,
  });
}

/** The line a row opened the peek from, handed to the cache so the peek paints at once (the by-id read confirms it). */
export function seedAuditEvent(queryClient: QueryClient, projectId: string, ev: AuditEvent): void {
  queryClient.setQueryData(auditKeys.event(projectId, ev.id), ev);
}
